"use client";

import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { HeroCtaButton } from "@/components/ui/hero-cta-button";
import { HERO_ENTRANCE_PERSPECTIVE } from "@/lib/hero-entrance-motion";
import {
  CONTAINER_BOUNCE_TRANSITION,
  SCENE_FINAL_POSE,
  SCENE_FINAL_TRANSITION,
} from "@/lib/transformation-organize-motion";

type Testimonial = {
  id: string;
  company: string;
  content: string;
  author: string;
  role: string;
};

const CONTENT_TEXT_CLASS = "text-[0.84rem] leading-[1.48] text-zinc-300 sm:text-[0.9rem]";

const HERO_GRADIENT_TEXT =
  "bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent";

const GLASS_CARD_CLASS =
  "flex flex-col rounded-2xl bg-[#1C1C1C] px-4 py-3 shadow-[0_16px_40px_-18px_rgba(0,0,0,0.42),0_6px_18px_-10px_rgba(0,0,0,0.28)] ring-1 ring-white/10 sm:px-5 sm:py-3.5";

const PANEL_SURFACE_CLASS =
  "pointer-events-none absolute inset-0 rounded-[1.75rem] bg-white/55 shadow-[0_24px_70px_-28px_rgba(15,23,42,0.28),0_8px_24px_-12px_rgba(15,23,42,0.12)] ring-1 ring-white/80 backdrop-blur-md md:rounded-[2rem]";

const EQUAL_GRID = "minmax(0, 1fr) minmax(0, 1fr)";
const ROW1_BENTO_GRID = "minmax(0, 0.82fr) minmax(0, 1.18fr)";
const ROW2_BENTO_GRID = "minmax(0, 1.18fr) minmax(0, 0.82fr)";

const SEQUENCE_EASE = [0.16, 1, 0.3, 1] as const;
const FADE_DURATION = 0.55;
const RESIZE_DELAY = 0.45;
const RESIZE_DURATION = 0.9;
const TEXT_DELAY = RESIZE_DELAY + RESIZE_DURATION;
const TEXT_DURATION = 0.45;

const bestTestimonials: Testimonial[] = [
  {
    id: "rec-1",
    company: "GreenBeamCraft",
    content:
      "WebDifference, c'est de l'écoute, de la créativité maîtrisée, une expertise qui me garantit un budget, une mise en œuvre et une performance prédictibles. La différence : aujourd'hui, je pilote mon business à temps plein.",
    author: "Jean Claude",
    role: "CEO chez GreenBeamCraft",
  },
  {
    id: "rec-2",
    company: "Signatures France",
    content:
      "Un immense merci à toute l'équipe de WebDifference pour son accompagnement remarquable dans la refonte de Signatures France. Grâce à eux, notre plateforme a gagné une identité plus moderne, plus fluide et plus professionnelle, tout en conservant notre âme et nos valeurs. Je recommande vivement cette agence à toutes les entreprises qui cherchent une équipe compétente et engagée.",
    author: "Samuel",
    role: "Signatures France",
  },
  {
    id: "rec-3",
    company: "Verdi Events",
    content:
      "Si vous cherchez du passable à bas coût, passez votre chemin. Pour de l'exceptionnel, au juste prix et dans les délais, vous êtes au bon endroit.",
    author: "Corinne",
    role: "CEO chez Verdi Events",
  },
  {
    id: "rec-4",
    company: "Bien à part",
    content:
      "Au début je pensais juste avoir besoin d'un site, mais WebDifference m'a surtout apporté des idées et des solutions auxquelles je n'avais pas pensé. Ils m'ont aidé à améliorer la structure, la visibilité et l'expérience utilisateur, avec une vraie logique derrière (SEO, conversion...). Si vous avez un projet immobilier ou un site à développer, je recommande clairement.",
    author: "Frédéric",
    role: "Bien à part",
  },
];

function AuthorAvatar({ author }: { author: string }) {
  const initial = author.trim().charAt(0).toUpperCase();

  return (
    <div
      aria-hidden
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-sm font-bold text-[#1C1C1C] sm:h-11 sm:w-11"
    >
      {initial}
    </div>
  );
}

function measureTextLineWidths(probe: HTMLParagraphElement): number[] {
  const containerWidth = probe.clientWidth;
  if (!containerWidth) return [100];

  const range = document.createRange();
  range.selectNodeContents(probe);
  const rects = Array.from(range.getClientRects());

  if (!rects.length) return [100];

  const lines = new Map<number, number>();
  for (const rect of rects) {
    const top = Math.round(rect.top);
    lines.set(top, Math.max(lines.get(top) ?? 0, rect.width));
  }

  return Array.from(lines.entries())
    .sort(([topA], [topB]) => topA - topB)
    .map(([, width]) => Math.min(100, Math.max(18, Math.round((width / containerWidth) * 100))));
}

function useMeasuredTextLines(content: string) {
  const containerRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLParagraphElement>(null);
  const [lineWidths, setLineWidths] = useState<number[]>([100]);

  const measure = useCallback(() => {
    const probe = probeRef.current;
    if (!probe) return;
    setLineWidths(measureTextLineWidths(probe));
  }, []);

  useLayoutEffect(() => {
    measure();
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => measure());
    observer.observe(container);
    return () => observer.disconnect();
  }, [content, measure]);

  return { containerRef, probeRef, lineWidths };
}

function TestimonialTextSkeleton({ lineWidths }: { lineWidths: number[] }) {
  return (
    <div className={`flex flex-col ${CONTENT_TEXT_CLASS}`} aria-hidden>
      {lineWidths.map((width, index) => (
        <div key={index} className="flex min-h-[1.5em] items-center">
          <div
            className="h-[0.58em] rounded-full bg-white/20 animate-pulse"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </div>
  );
}

function TestimonialCard({
  testimonial,
  showContent,
}: {
  testimonial: Testimonial;
  showContent: boolean;
}) {
  const { containerRef, probeRef, lineWidths } = useMeasuredTextLines(testimonial.content);

  return (
    <article className={GLASS_CARD_CLASS}>
      <div className="mb-2 flex min-w-0 items-center gap-2.5 sm:gap-3">
        <AuthorAvatar author={testimonial.author} />
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-white">
            {testimonial.author}
          </p>
          <p className="mt-0.5 truncate text-xs text-zinc-400 sm:text-sm">{testimonial.company}</p>
        </div>
      </div>

      <div ref={containerRef} className="relative">
        <p
          ref={probeRef}
          aria-hidden
          className={`${CONTENT_TEXT_CLASS} pointer-events-none invisible absolute inset-x-0 top-0 w-full`}
        >
          {testimonial.content}
        </p>

        <AnimatePresence mode="wait">
          {!showContent ? (
            <div key="skeleton">
              <TestimonialTextSkeleton lineWidths={lineWidths} />
            </div>
          ) : (
            <motion.p
              key="content"
              className={CONTENT_TEXT_CLASS}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: TEXT_DURATION, ease: SEQUENCE_EASE }}
            >
              {testimonial.content}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </article>
  );
}

const [greenBeamCraft, signaturesFrance, verdiEvents, bienAPart] = bestTestimonials;

export default function ReviewsRecommendationsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  const reducedMotion = useReducedMotion();
  const [isDesktop, setIsDesktop] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 640px)");
    const update = () => setIsDesktop(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  const textDelay = reducedMotion ? 0 : isDesktop ? TEXT_DELAY : FADE_DURATION;

  useEffect(() => {
    if (!isInView) return;

    if (reducedMotion) {
      setShowContent(true);
      return;
    }

    const timer = window.setTimeout(() => setShowContent(true), textDelay * 1000);
    return () => window.clearTimeout(timer);
  }, [isInView, reducedMotion, textDelay]);

  return (
    <section
      ref={sectionRef}
      data-scroll-center
      className="flex min-h-[calc(100svh-5rem-env(safe-area-inset-top))] flex-col justify-center bg-[#f3f3f3] py-4 sm:py-5"
    >
      <div className="mx-auto w-full max-w-6xl px-6">
        <div
          className="relative mx-auto w-full max-w-[960px] overflow-visible"
          style={{
            perspective: HERO_ENTRANCE_PERSPECTIVE,
            perspectiveOrigin: "50% 42%",
          }}
        >
          <motion.div
            className="pointer-events-none absolute -inset-x-8 -inset-y-8 rounded-[2.5rem] bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.14)_0%,transparent_72%)] blur-3xl"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: reducedMotion ? 0.2 : 0.38, ease: SEQUENCE_EASE }}
          />

          <motion.div
            className="relative flex flex-col gap-5 sm:gap-6"
            initial={false}
            animate={
              isInView
                ? reducedMotion
                  ? { rotateX: 0, rotateY: 0 }
                  : SCENE_FINAL_POSE
                : { rotateX: 0, rotateY: 0 }
            }
            transition={reducedMotion ? { duration: 0.2, ease: "easeOut" } : SCENE_FINAL_TRANSITION}
            style={{ transformStyle: "preserve-3d" }}
          >
            <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-2 text-center sm:gap-2.5">
              <motion.div
                initial={false}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
                transition={{ duration: FADE_DURATION, ease: SEQUENCE_EASE }}
              >
                <h2 className="mx-auto max-w-4xl text-balance text-[1.5rem] font-bold leading-[1.08] tracking-[-0.04em] text-[#1C1C1C] sm:text-3xl md:text-4xl md:leading-[1.05]">
                  Et si c&apos;était{" "}
                  <span className={HERO_GRADIENT_TEXT}>vous ?</span>
                </h2>
              </motion.div>

              <motion.p
                className="text-sm font-medium text-zinc-500 sm:text-base"
                initial={false}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
                transition={
                  reducedMotion
                    ? { duration: 0 }
                    : { delay: 0.12, duration: FADE_DURATION, ease: SEQUENCE_EASE }
                }
              >
                100% de nos clients nous notent 5 étoiles.
              </motion.p>
            </div>

            <div className="relative overflow-visible" style={{ transformStyle: "preserve-3d" }}>
              <motion.div
                className={PANEL_SURFACE_CLASS}
                aria-hidden="true"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={
                  isInView
                    ? { opacity: 1, scale: reducedMotion ? 1 : [0.9, 1.05, 1] }
                    : { opacity: 0, scale: 0.9 }
                }
                transition={
                  reducedMotion ? { duration: 0.2, ease: "easeOut" } : CONTAINER_BOUNCE_TRANSITION
                }
                style={{ transformOrigin: "50% 50%" }}
              />

              <div className="relative overflow-visible p-2.5 sm:p-3 md:p-4">
                <motion.div
                  className="flex flex-col gap-3 sm:gap-3.5"
                  initial={false}
                  animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: FADE_DURATION, ease: SEQUENCE_EASE }}
                >
                  <div className="grid grid-cols-1 gap-3 sm:hidden">
                    <TestimonialCard testimonial={greenBeamCraft} showContent={showContent} />
                    <TestimonialCard testimonial={signaturesFrance} showContent={showContent} />
                    <TestimonialCard testimonial={bienAPart} showContent={showContent} />
                    <TestimonialCard testimonial={verdiEvents} showContent={showContent} />
                  </div>

                  <div className="hidden flex-col gap-3.5 sm:flex">
                    <motion.div
                      className="grid gap-3.5"
                      initial={false}
                      animate={
                        isInView
                          ? { gridTemplateColumns: ROW1_BENTO_GRID }
                          : { gridTemplateColumns: EQUAL_GRID }
                      }
                      transition={
                        reducedMotion
                          ? { duration: 0 }
                          : { delay: RESIZE_DELAY, duration: RESIZE_DURATION, ease: SEQUENCE_EASE }
                      }
                      style={{ gridTemplateColumns: EQUAL_GRID }}
                    >
                      <TestimonialCard testimonial={greenBeamCraft} showContent={showContent} />
                      <TestimonialCard testimonial={signaturesFrance} showContent={showContent} />
                    </motion.div>

                    <motion.div
                      className="grid gap-3.5"
                      initial={false}
                      animate={
                        isInView
                          ? { gridTemplateColumns: ROW2_BENTO_GRID }
                          : { gridTemplateColumns: EQUAL_GRID }
                      }
                      transition={
                        reducedMotion
                          ? { duration: 0 }
                          : { delay: RESIZE_DELAY, duration: RESIZE_DURATION, ease: SEQUENCE_EASE }
                      }
                      style={{ gridTemplateColumns: EQUAL_GRID }}
                    >
                      <TestimonialCard testimonial={bienAPart} showContent={showContent} />
                      <TestimonialCard testimonial={verdiEvents} showContent={showContent} />
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>

            <motion.div
              className="flex justify-center"
              initial={false}
              animate={showContent ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={
                reducedMotion
                  ? { duration: 0 }
                  : { duration: TEXT_DURATION, ease: SEQUENCE_EASE }
              }
            >
              <HeroCtaButton>Je veux le même résultat</HeroCtaButton>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
